"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@packages/ui/src/components/shadcn/carousel";
import { ContainerGlob } from "../../../componentsGLob/ContainerGlob";
import { Card } from "./Card";
import { cn } from "@/lib/utils";

export const OurSpacesSection = () => {
  const autoplay = React.useRef(
    Autoplay({ delay: 4000, stopOnInteraction: true, stopOnMouseEnter: true })
  );

  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  const { t } = useTranslation("spaces");

  React.useEffect(() => {
    if (!api) return;

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  // Dati tradotti
  const translatedCards = t("cards", { returnObjects: true }) as {
    title: string;
    description: string;
  }[];

  const images = [
    "/carousel/cowoCarousel.png",
    "/carousel/cowoCarousel2.png",
    "/carousel/cowoCarousel3.png",
  ];

  const CARDS = translatedCards.map((card, index) => ({
    image: images[index],
    title: card.title,
    description: card.description,
  }));

  return (
    <section id="spazi" className="py-16 bg-white">
      <ContainerGlob>
        <motion.div
          className="mx-auto mb-16 max-w-3xl text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="mb-4 text-3xl font-bold text-gray-900">
            {t("sectionTitle")}
          </h2>
          <p className="text-lg leading-relaxed text-gray-600">
            {t("sectionDescription")}
          </p>
        </motion.div>

        <div className="md:hidden">
          <Carousel
            setApi={setApi}
            opts={{
              align: "center",
              loop: true,
              dragFree: false,
            }}
            plugins={[autoplay.current]}
            className="w-full"
          >
            <CarouselContent className="-ml-3">
              {CARDS.map((card) => (
                <CarouselItem key={card.title} className="basis-11/12 pl-3">
                  <Card {...card} />
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>

          <div className="mt-6 flex items-center justify-center gap-2">
            {Array.from({ length: count }).map((_, index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className={cn(
                  "h-2.5 w-2.5 rounded-full transition-all duration-300 hover:scale-110",
                  {
                    "bg-blue-600 scale-125": current === index + 1,
                    "bg-gray-300 hover:bg-gray-400": current !== index + 1,
                  }
                )}
                aria-label={`${t("slideLabel")} ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="hidden md:mx-auto md:grid md:max-w-6xl md:grid-cols-2 md:gap-8 lg:grid-cols-3">
          {CARDS.map((card) => (
            <Card key={card.title} {...card} />
          ))}
        </div>
      </ContainerGlob>
    </section>
  );
};

export default OurSpacesSection;
